import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { QRCodeSVG } from 'qrcode.react'
import truemoneyLogo from '@/assets/truemoney-logo.png'
import { getThemeById } from '../config/themes'
import type { TenantThemeConfig } from '../types/theme'
import * as authService from '../services/authService'
import {
  getDepositChannels,
  getDepositPresetAmounts,
  syncPresetAmountsAfterDepositChannelsUpdate,
  getDecimalReservationCountdownMinutes,
  getPublicDepositAccounts,
  getPublicPromptPayAccounts,
  isPromptPayDepositAccount,
  isTrueMoneyDepositAccount,
  mapPromptPayAccountsToDepositAccounts,
  resolveDepositAccountForAmount,
  type DepositChannels,
  type PublicDepositAccount,
} from '../services/depositAccountService'
import { getDepositFlowConfig, type DepositFlowConfig } from '../services/depositFlowService'
import { createDecimalReservation, cancelDecimalReservation } from '../services/decimalDepositService'
import {
  assertDepositResponseMatchesDraft,
  attachSlipToDepositTransaction,
  createDeposit,
  refreshPaymentGatewayTransaction,
  type WalletDepositResponse,
} from '../services/walletTransactionService'
import { slipStorageService } from '../services/slipStorageService'
import {
  prepareSlipFileForUpload,
  SLIP_UPLOAD_HINT,
  slipCompressedMessage,
} from '@huay/shared/slipImage'
import { buildPromptPayQrValue } from '../utils/promptPayQr'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface BottomDepositFlowModalProps {
  isOpen: boolean
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
  userBankAccounts: authService.UserBankAccount[]
  onClose: () => void
  onCompleted?: () => void
}

type DepositStep = 1 | 2 | 3 | 4
type PendingDepositMethod = 'bank_transfer' | 'prompt_pay' | 'true_money' | 'decimal_reservation'

interface TopupMethodOption {
  method: PendingDepositMethod
  label: string
  icon: string
  accent: string
}

const DEFAULT_PRESET_AMOUNTS = [100, 200, 300, 500, 1000, 2000, 3000, 5000, 10000]
const DEFAULT_DEPOSIT_CHANNELS: DepositChannels = {
  bank: true,
  trueMoney: true,
  promptPay: true,
  decimalReservation: true,
  allowSubmitWithoutSlip: true,
}
const DEFAULT_DEPOSIT_FLOW: DepositFlowConfig = {
  tenantId: '',
  tenantName: '',
  paymentServiceSelected: false,
  paymentGatewayEnabled: false,
  slipServiceSelected: false,
  slipVerificationEnabled: false,
  effectiveDepositMode: 'manual',
  depositAutoCreditEnabled: true,
  depositAutoCreditViaPayment: true,
  depositAutoCreditViaSlip: true,
  depositAutoCreditViaSms: true,
  automationSettingsWarning: null,
}

const createDraftId = () => `bottom-deposit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

const formatSecondsAsCountdown = (seconds: number): string => {
  const safeSeconds = Math.max(0, Math.floor(seconds))
  const minutes = Math.floor(safeSeconds / 60)
  const remainingSeconds = safeSeconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`
}

const modalShellStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 100050,
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  isolation: 'isolate',
  pointerEvents: 'none',
  padding: '1rem',
  paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 6.5rem)',
}

const modalCardStyle: React.CSSProperties = {
  width: 'min(720px, 100%)',
  maxHeight: 'min(calc(100vh - 8rem), 860px)',
  position: 'relative',
  zIndex: 1,
  pointerEvents: 'auto',
  background: 'linear-gradient(180deg, rgba(5, 10, 25, 0.98) 0%, rgba(15, 23, 42, 0.98) 100%)',
  borderRadius: 28,
  border: '1px solid rgba(56, 189, 248, 0.22)',
  boxShadow: '0 30px 80px rgba(0, 0, 0, 0.45)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
}

const panelStyle: React.CSSProperties = {
  background: 'linear-gradient(180deg, rgba(8,15,30,0.96) 0%, rgba(15,23,42,0.94) 100%)',
  boxShadow: '0 18px 38px rgba(15, 23, 42, 0.24)',
}

const innerSurfaceStyle: React.CSSProperties = {
  background: 'linear-gradient(180deg, rgba(255,255,255,0.99) 0%, rgba(241,245,249,0.96) 100%)',
  boxShadow: '0 14px 30px rgba(15, 23, 42, 0.08), inset 0 1px 0 rgba(255,255,255,0.9)',
}

const valueCardStyle: React.CSSProperties = {
  ...innerSurfaceStyle,
  borderRadius: 22,
  padding: '1rem',
}

const lightLabelStyle: React.CSSProperties = {
  color: '#64748b',
  fontWeight: 600,
}

const lightTextStyle: React.CSSProperties = {
  color: '#475569',
  fontWeight: 500,
}

const lightHeadingStyle: React.CSSProperties = {
  color: '#0f172a',
}

const stepCardStyle = (active: boolean): React.CSSProperties => ({
  borderRadius: 18,
  padding: '0.85rem 0.95rem',
  border: active ? '1px solid rgba(56, 189, 248, 0.28)' : '1px solid rgba(148, 163, 184, 0.18)',
  background: active
    ? 'linear-gradient(135deg, #2563eb 0%, #38bdf8 100%)'
    : 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(226,232,240,0.92) 100%)',
  boxShadow: active ? '0 16px 30px rgba(37, 99, 235, 0.24)' : '0 10px 24px rgba(15, 23, 42, 0.08)',
})

const amountButtonStyle = (selected: boolean): React.CSSProperties => ({
  minHeight: 80,
  borderRadius: 18,
  padding: '0.9rem 0.85rem',
  border: selected ? '1px solid rgba(37, 99, 235, 0.18)' : '1px solid rgba(15, 23, 42, 0.08)',
  background: selected
    ? 'linear-gradient(135deg, #2563eb 0%, #38bdf8 100%)'
    : 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(241,245,249,0.95) 100%)',
  boxShadow: selected
    ? '0 18px 32px rgba(37, 99, 235, 0.28)'
    : '0 10px 24px rgba(15, 23, 42, 0.10), inset 0 1px 0 rgba(255,255,255,0.92)',
  transform: selected ? 'translateY(-2px)' : 'translateY(0)',
  transition: 'all 0.2s ease',
})

const methodCardStyle = (active: boolean, accent: string): React.CSSProperties => ({
  ...valueCardStyle,
  border: active ? `1px solid ${accent}` : '1px solid rgba(148, 163, 184, 0.18)',
  boxShadow: active
    ? `0 18px 32px ${accent}33, inset 0 1px 0 rgba(255,255,255,0.92)`
    : valueCardStyle.boxShadow,
  transform: active ? 'translateY(-2px)' : 'translateY(0)',
  transition: 'all 0.2s ease',
})

const getMethodLabel = (method: PendingDepositMethod, isPaymentMode = false): string => {
  if (isPaymentMode && method === 'bank_transfer') return 'Payment'
  if (method === 'true_money') return 'TrueMoney Wallet'
  if (method === 'prompt_pay') return 'PromptPay'
  if (method === 'decimal_reservation') return 'ฝากทศนิยม'
  return 'โอนผ่านธนาคาร'
}

const getStepTitle = (step: DepositStep, method: PendingDepositMethod | null, isPaymentMode = false): string => {
  if (step === 1) return 'เลือกวิธีการฝาก'
  if (step === 2) return 'เลือกยอดฝาก'
  if (step === 3) {
    if (isPaymentMode) return 'ชำระผ่าน Payment'
    if (method === 'prompt_pay') return 'สแกน QR PromptPay'
    if (method === 'true_money') return 'โอนเข้า TrueMoney'
    return 'ตรวจสอบรายละเอียดการโอน'
  }
  return 'แนบสลิป'
}

const getStepDescription = (step: DepositStep, method: PendingDepositMethod | null, isPaymentMode = false): string => {
  if (step === 1) {
    return 'เลือกวิธีการฝากที่ต้องการ แล้วระบบจะพาไปตามขั้นตอนเดียวกับหน้าฝากเงินหลัก'
  }
  if (step === 2) {
    return 'ระบุจำนวนเงินที่ต้องการฝาก ก่อนตรวจสอบรายละเอียดการชำระเงินหรือบัญชีปลายทาง'
  }
  if (step === 3) {
    if (isPaymentMode) {
      return 'ตรวจสอบยอดและสแกน QR Payment ให้เรียบร้อย ก่อนเข้าสู่ขั้นตอนแนบสลิป'
    }
    if (method === 'prompt_pay') {
      return 'สแกน QR PromptPay หรือคัดลอกข้อมูลปลายทาง จากนั้นไปยังขั้นตอนแนบสลิป'
    }
    if (method === 'true_money') {
      return 'ตรวจสอบบัญชี TrueMoney ปลายทางและยอดโอน ก่อนเข้าสู่ขั้นตอนแนบสลิป'
    }
    if (method === 'decimal_reservation') {
      return 'ตรวจสอบยอดทศนิยมและเวลานับถอยหลัง จากนั้นไปยังขั้นตอนแนบสลิป'
    }
    return 'ตรวจสอบบัญชีธนาคารปลายทางและยอดโอน ก่อนเข้าสู่ขั้นตอนแนบสลิป'
  }
  return 'แนบสลิปเพื่อส่งรายการฝากให้ครบขั้นตอน จากนั้นระบบจะปิดหน้าต่างนี้ให้อัตโนมัติ'
}

const getReviewStepLabel = (method: PendingDepositMethod | null, isPaymentMode = false): string => {
  if (isPaymentMode) return 'ชำระผ่าน Payment'
  if (method === 'prompt_pay') return 'สแกน QR PromptPay'
  if (method === 'true_money') return 'โอนเข้า TrueMoney'
  return 'ตรวจสอบรายละเอียด'
}

const formatCurrencyLabel = (amount: number | string): string => {
  const numeric = typeof amount === 'number' ? amount : Number(amount)
  return Number.isFinite(numeric)
    ? `${numeric.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท`
    : '-'
}

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

const showToast = (icon: 'success' | 'error' | 'warning' | 'info', title: string) =>
  Swal.fire({
    toast: true,
    position: 'top',
    timer: 2500,
    showConfirmButton: false,
    icon,
    title,
  })

const BottomDepositFlowModal: React.FC<BottomDepositFlowModalProps> = ({
  isOpen,
  themeConfig,
  assetConfig,
  userBankAccounts,
  onClose,
  onCompleted,
}) => {
  const navigate = useNavigate()
  const currentUser = authService.getCurrentUser()
  const tenantId = currentUser?.tenantId || themeConfig.tenantId || ''
  const userId = currentUser?.id || ''
  const username = currentUser?.username || ''
  const primaryUserBankAccount = userBankAccounts[0] ?? null

  const [step, setStep] = useState<DepositStep>(1)
  const [selectedMethod, setSelectedMethod] = useState<PendingDepositMethod | null>(null)
  const [amountInput, setAmountInput] = useState('')
  const [depositAccounts, setDepositAccounts] = useState<PublicDepositAccount[]>([])
  const [promptPayAccounts, setPromptPayAccounts] = useState<PublicDepositAccount[]>([])
  const [depositChannels, setDepositChannels] = useState<DepositChannels>(DEFAULT_DEPOSIT_CHANNELS)
  const [presetAmounts, setPresetAmounts] = useState<number[]>(DEFAULT_PRESET_AMOUNTS)
  const [depositFlow, setDepositFlow] = useState<DepositFlowConfig>(DEFAULT_DEPOSIT_FLOW)
  const [configLoading, setConfigLoading] = useState(false)
  const [draftId, setDraftId] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [paymentTransaction, setPaymentTransaction] = useState<WalletDepositResponse['data'] | null>(null)
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [paymentRefreshing, setPaymentRefreshing] = useState(false)
  const [paymentError, setPaymentError] = useState('')
  const [paymentRefreshMessage, setPaymentRefreshMessage] = useState('')
  const [countdownNowMs, setCountdownNowMs] = useState(() => Date.now())
  const [reservationId, setReservationId] = useState<string | null>(null)
  const [reservationAmount, setReservationAmount] = useState<string>('')
  const [reservationRequestedAmount, setReservationRequestedAmount] = useState<string>('')
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null)

  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const decimalReservationStartedRef = useRef(false)
  const timeoutHandledRef = useRef(false)

  const themeFolder = useMemo(() => {
    const meta = getThemeById(themeConfig.themeId)
    return meta?.folder ?? 'HuayToday-Blue'
  }, [themeConfig.themeId])

  const getAssetUrl = useCallback((path: string) => {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`
    const base = assetConfig.CDN_BASE_URL ? `${assetConfig.CDN_BASE_URL}/${themeFolder}` : `/${themeFolder}`
    return `${base}${normalizedPath}`
  }, [assetConfig.CDN_BASE_URL, themeFolder])

  const getDepositAccountLogoUrl = useCallback((account: PublicDepositAccount): string => {
    if (account.bankIcon) return account.bankIcon
    const code = (account.bankCode || '').toUpperCase()
    const name = (account.bankName || '').toLowerCase()
    if (code === 'TRUE' || name.includes('true money') || name.includes('truemoney')) {
      return typeof truemoneyLogo === 'string' ? truemoneyLogo : (truemoneyLogo as unknown as string)
    }
    return getAssetUrl(`/img/b/${code || 'KBANK'}.png`)
  }, [getAssetUrl])

  const enteredAmount = useMemo(() => {
    const numeric = Number(amountInput.trim())
    return Number.isFinite(numeric) ? numeric : NaN
  }, [amountInput])

  const defaultBankAccount = useMemo(
    () =>
      depositAccounts.find((account) => !isTrueMoneyDepositAccount(account) && !isPromptPayDepositAccount(account) && account.isDefault)
      ?? depositAccounts.find((account) => !isTrueMoneyDepositAccount(account) && !isPromptPayDepositAccount(account))
      ?? null,
    [depositAccounts]
  )

  const selectedBankAccount = useMemo(
    () => resolveDepositAccountForAmount(depositAccounts, enteredAmount, { channel: 'bank' }) ?? defaultBankAccount,
    [defaultBankAccount, depositAccounts, enteredAmount]
  )
  const selectedTrueMoneyAccount = useMemo(
    () => resolveDepositAccountForAmount(depositAccounts, enteredAmount, { channel: 'true_money' }),
    [depositAccounts, enteredAmount]
  )
  const selectedPromptPayAccount = useMemo(
    () => resolveDepositAccountForAmount(promptPayAccounts, enteredAmount, { channel: 'promptpay' }),
    [enteredAmount, promptPayAccounts]
  )

  const isPaymentMode = selectedMethod === 'bank_transfer' && depositFlow.paymentGatewayEnabled
  const selectedManualAccount = useMemo(() => {
    if (selectedMethod === 'true_money') return selectedTrueMoneyAccount
    if (selectedMethod === 'prompt_pay') return selectedPromptPayAccount
    return selectedBankAccount
  }, [selectedBankAccount, selectedMethod, selectedPromptPayAccount, selectedTrueMoneyAccount])

  const effectiveAmount = selectedMethod === 'decimal_reservation' ? Number(reservationAmount || 0) : Number(amountInput || 0)
  const effectiveAmountDisplay = formatCurrencyLabel(effectiveAmount)
  const promptPayQrValue = useMemo(
    () =>
      selectedMethod === 'prompt_pay'
        ? buildPromptPayQrValue(selectedPromptPayAccount?.accountNumber || '', enteredAmount)
        : null,
    [enteredAmount, selectedMethod, selectedPromptPayAccount?.accountNumber]
  )
  const paymentGateway = paymentTransaction?.metadata?.paymentGateway
  const paymentReference = paymentGateway?.externalRefId || paymentTransaction?._id || '-'
  const paymentQrImage = paymentGateway?.qrImage || ''
  const paymentQrRawData = paymentGateway?.qrRawData || paymentGateway?.qrcodePayload?.qrcode_text || ''
  const paymentQrLink = paymentGateway?.qrUrl || paymentGateway?.qrcodePayload?.link || ''
  const hasPaymentQr = Boolean(paymentQrImage || paymentQrRawData)
  const paymentReuseExpiresAtMs = useMemo(() => {
    const rawValue = paymentGateway?.reuseExpiresAt || ''
    const parsed = rawValue ? new Date(rawValue).getTime() : 0
    return Number.isFinite(parsed) ? parsed : 0
  }, [paymentGateway?.reuseExpiresAt])
  const paymentReuseSecondsLeft = paymentReuseExpiresAtMs > 0
    ? Math.max(0, Math.floor((paymentReuseExpiresAtMs - countdownNowMs) / 1000))
    : 0
  const paymentReuseCountdownText = formatSecondsAsCountdown(paymentReuseSecondsLeft)
  const allowSubmitWithoutSlip = depositChannels.allowSubmitWithoutSlip !== false && !isPaymentMode
  const canProceedToSlip = isPaymentMode ? Boolean(paymentTransaction?._id) && hasPaymentQr && !paymentLoading && !paymentRefreshing && !paymentError : true

  useEffect(() => {
    if (paymentReuseSecondsLeft <= 0) return
    const intervalId = window.setInterval(() => setCountdownNowMs(Date.now()), 1000)
    return () => window.clearInterval(intervalId)
  }, [paymentReuseSecondsLeft])

  const methodOptions = useMemo<TopupMethodOption[]>(() => {
    const nextOptions: TopupMethodOption[] = []
    if (depositChannels.bank) {
      nextOptions.push({
        method: 'bank_transfer',
        label: depositFlow.paymentGatewayEnabled ? 'ธนาคาร / Payment' : 'ธนาคาร',
        icon: 'fas fa-university',
        accent: '#2563eb',
      })
    }
    if (depositChannels.trueMoney && depositAccounts.some((account) => isTrueMoneyDepositAccount(account))) {
      nextOptions.push({
        method: 'true_money',
        label: 'TrueMoney',
        icon: 'fa-solid fa-wallet',
        accent: '#f97316',
      })
    }
    if (depositChannels.promptPay && promptPayAccounts.length > 0) {
      nextOptions.push({
        method: 'prompt_pay',
        label: 'PromptPay',
        icon: 'fa-solid fa-qrcode',
        accent: '#0ea5e9',
      })
    }
    if (depositChannels.decimalReservation) {
      nextOptions.push({
        method: 'decimal_reservation',
        label: 'ฝากทศนิยม',
        icon: 'fa-solid fa-stopwatch',
        accent: '#8b5cf6',
      })
    }
    return nextOptions
  }, [depositAccounts, depositChannels, depositFlow.paymentGatewayEnabled, promptPayAccounts.length])

  const resetTransientState = useCallback((clearMethodAndAmount = false) => {
    setPaymentTransaction(null)
    setPaymentLoading(false)
    setPaymentRefreshing(false)
    setPaymentError('')
    setPaymentRefreshMessage('')
    setReservationId(null)
    setReservationAmount('')
    setReservationRequestedAmount('')
    setSecondsLeft(null)
    decimalReservationStartedRef.current = false
    timeoutHandledRef.current = false
    setSelectedFile(null)
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
    }
    setPreviewUrl(null)
    setIsDragOver(false)
    setSubmitting(false)
    if (clearMethodAndAmount) {
      setStep(1)
      setSelectedMethod(null)
      setAmountInput('')
      setDraftId('')
    }
  }, [previewUrl])

  const handleModalCompleted = useCallback((message: string) => {
    void showToast('success', message)
    onCompleted?.()
    onClose()
    resetTransientState(true)
  }, [onClose, onCompleted, resetTransientState])

  const cleanupReservation = useCallback(async () => {
    if (!reservationId) return
    await cancelDecimalReservation(reservationId).catch(() => {})
  }, [reservationId])

  const requestClose = useCallback(async () => {
    const hasProgress = step > 1 || Boolean(selectedMethod) || Boolean(amountInput) || Boolean(paymentTransaction?._id) || Boolean(reservationId)
    if (!hasProgress) {
      onClose()
      resetTransientState(true)
      return
    }

    const result = await Swal.fire({
      icon: 'question',
      title: 'ปิดหน้าฝากเงิน?',
      text: 'หากปิดตอนนี้ ข้อมูลที่กำลังกรอกจะหายไป',
      showCancelButton: true,
      confirmButtonText: 'ปิดหน้าต่าง',
      cancelButtonText: 'อยู่ต่อ',
    })

    if (!result.isConfirmed) return
    if (selectedMethod === 'decimal_reservation' && reservationId) {
      await cleanupReservation()
    }
    onClose()
    resetTransientState(true)
  }, [amountInput, cleanupReservation, onClose, paymentTransaction?._id, reservationId, resetTransientState, selectedMethod, step])

  useEffect(() => {
    if (!isOpen || !tenantId) return
    setConfigLoading(true)
    Promise.all([
      getPublicDepositAccounts(tenantId),
      getPublicPromptPayAccounts(tenantId),
      getDepositChannels(tenantId),
      getDepositPresetAmounts(tenantId),
      getDepositFlowConfig(tenantId),
    ])
      .then(([accounts, promptPayList, channels, presetAmountsResponse, flow]) => {
        setDepositAccounts(accounts)
        setPromptPayAccounts(mapPromptPayAccountsToDepositAccounts(promptPayList))
        setDepositChannels(channels)
        setPresetAmounts(presetAmountsResponse)
        setDepositFlow(flow)
      })
      .finally(() => setConfigLoading(false))
  }, [isOpen, tenantId])

  useEffect(() => {
    if (!tenantId) return

    const onDepositChannelsUpdated = (
      event: CustomEvent<{
        tenantId?: string
        content?: {
          depositChannels?: Partial<DepositChannels> & {
            quickDepositEnabled?: boolean
            presetAmounts?: number[]
          }
          settings?: {
            depositChannels?: Partial<DepositChannels> & {
              quickDepositEnabled?: boolean
              presetAmounts?: number[]
            }
          }
        }
      }>
    ) => {
      if (event.detail?.tenantId && event.detail.tenantId !== tenantId) return
      const contentChannels =
        event.detail?.content?.depositChannels || event.detail?.content?.settings?.depositChannels
      if (contentChannels && typeof contentChannels === 'object') {
        setDepositChannels((prev) => ({
          ...prev,
          bank: contentChannels.bank !== false,
          trueMoney: contentChannels.trueMoney !== false,
          promptPay: contentChannels.promptPay !== false,
          decimalReservation: contentChannels.decimalReservation !== false,
          allowSubmitWithoutSlip: contentChannels.allowSubmitWithoutSlip !== false,
        }))
      } else {
        getDepositChannels(tenantId, { forceRefresh: true }).then(setDepositChannels)
      }
      void syncPresetAmountsAfterDepositChannelsUpdate(tenantId, contentChannels).then(setPresetAmounts)
      getDepositFlowConfig(tenantId).then(setDepositFlow)
    }

    window.addEventListener('deposit_channels_updated', onDepositChannelsUpdated as EventListener)
    return () => {
      window.removeEventListener('deposit_channels_updated', onDepositChannelsUpdated as EventListener)
    }
  }, [tenantId])

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  useEffect(() => {
    if (!isOpen || step !== 3 || selectedMethod !== 'decimal_reservation') return
    if (!tenantId || !draftId || !Number.isFinite(enteredAmount) || enteredAmount <= 0) return
    if (!selectedBankAccount?.accountNumber) return
    if (decimalReservationStartedRef.current || reservationId || paymentLoading) return
    decimalReservationStartedRef.current = true

    let active = true
    Promise.all([getDecimalReservationCountdownMinutes(tenantId)])
      .then(([minutes]) =>
        createDecimalReservation(Math.floor(enteredAmount), {
          expiresInMinutes: minutes,
          depositorName: primaryUserBankAccount?.accountName,
          depositorBank: primaryUserBankAccount?.bankName || primaryUserBankAccount?.bankCode,
          depositorAccount: primaryUserBankAccount?.accountNumber,
          webAccountName: selectedBankAccount.accountName,
          webAccountNumber: selectedBankAccount.accountNumber,
          webBankName: selectedBankAccount.bankName,
        })
      )
      .then((res) => {
        if (!active) return
        if (!res.success || !res.data) {
          throw new Error(res.error || 'สร้างการจองฝากทศนิยมไม่สำเร็จ')
        }
        setReservationId(res.data.reservationId)
        setReservationAmount(String(res.data.amount))
        setReservationRequestedAmount(String(res.data.requestedAmount ?? Math.floor(enteredAmount)))
        const expiresAt = new Date(res.data.expiresAt).getTime()
        setSecondsLeft(Math.max(0, Math.floor((expiresAt - Date.now()) / 1000)))
      })
      .catch((error) => {
        decimalReservationStartedRef.current = false
        setStep(2)
        void showToast('error', error instanceof Error ? error.message : 'สร้างการจองฝากทศนิยมไม่สำเร็จ')
      })

    return () => {
      active = false
    }
  }, [draftId, enteredAmount, isOpen, paymentLoading, primaryUserBankAccount?.accountName, primaryUserBankAccount?.accountNumber, primaryUserBankAccount?.bankCode, primaryUserBankAccount?.bankName, reservationId, selectedBankAccount, selectedMethod, step, tenantId])

  const requestPaymentQr = useCallback(async () => {
    if (!tenantId || !userId || !draftId || !Number.isFinite(enteredAmount) || enteredAmount <= 0) {
      setPaymentError('ข้อมูลรายการฝากไม่ครบถ้วน')
      return
    }
    if (!primaryUserBankAccount?.bankCode || !primaryUserBankAccount?.accountNumber) {
      setPaymentError('ไม่พบบัญชีธนาคารของผู้ใช้')
      return
    }

    setPaymentLoading(true)
    setPaymentError('')
    setPaymentRefreshMessage('')
    try {
      const result = await createDeposit({
        amount: Number(enteredAmount),
        paymentMethod: 'payment_gateway',
        reference: draftId,
        metadata: {
          note: 'bottom nav modal created payment QR request',
          depositMethod: selectedMethod,
          sourceChannel: 'payment',
          paymentChannel: 'PPQR',
          userMobile: currentUser?.mobile,
          userBankAccount: {
            bankCode: primaryUserBankAccount.bankCode,
            bankName: primaryUserBankAccount.bankName,
            accountNumber: primaryUserBankAccount.accountNumber,
            accountName: primaryUserBankAccount.accountName,
          },
        },
      })

      if (!result.success || !result.data) {
        throw new Error(result.message || 'สร้าง QR ชำระเงินไม่สำเร็จ')
      }

      setPaymentTransaction(result.data)
    } catch (error) {
      setPaymentTransaction(null)
      setPaymentError(error instanceof Error ? error.message : 'สร้าง QR ชำระเงินไม่สำเร็จ')
    } finally {
      setPaymentLoading(false)
    }
  }, [currentUser?.mobile, draftId, enteredAmount, primaryUserBankAccount, selectedMethod, tenantId, userId])

  const refreshExistingPayment = useCallback(async () => {
    if (!paymentTransaction?._id) return
    setPaymentRefreshing(true)
    setPaymentRefreshMessage('')
    try {
      const result = await refreshPaymentGatewayTransaction(paymentTransaction._id)
      if (!result.success || !result.data?.transaction) {
        throw new Error(result.message || 'รีเฟรชข้อมูล Payment ไม่สำเร็จ')
      }
      const refreshedTransaction = result.data.transaction as NonNullable<WalletDepositResponse['data']>
      setPaymentTransaction(refreshedTransaction)
      if (
        !(
          refreshedTransaction.metadata?.paymentGateway?.qrImage
          || refreshedTransaction.metadata?.paymentGateway?.qrRawData
          || refreshedTransaction.metadata?.paymentGateway?.qrcodePayload?.qrcode_text
        )
      ) {
        setPaymentRefreshMessage('ยังไม่พบ QR ล่าสุดจากผู้ให้บริการ กรุณาลองอีกครั้งในอีกสักครู่')
      }
    } catch (error) {
      setPaymentRefreshMessage(error instanceof Error ? error.message : 'รีเฟรชข้อมูล Payment ไม่สำเร็จ')
    } finally {
      setPaymentRefreshing(false)
    }
  }, [paymentTransaction?._id])

  useEffect(() => {
    if (!isOpen || step !== 3 || !isPaymentMode) return
    if (paymentTransaction || paymentLoading || paymentRefreshing || paymentError) return
    void requestPaymentQr()
  }, [isOpen, isPaymentMode, paymentError, paymentLoading, paymentRefreshing, paymentTransaction, requestPaymentQr, step])

  useEffect(() => {
    if (secondsLeft == null || secondsLeft <= 0) return
    const timer = window.setInterval(() => {
      setSecondsLeft((prev) => (prev == null || prev <= 0 ? 0 : prev - 1))
    }, 1000)
    return () => window.clearInterval(timer)
  }, [secondsLeft])

  useEffect(() => {
    if (selectedMethod !== 'decimal_reservation') return
    if (secondsLeft !== 0 || timeoutHandledRef.current || !reservationId) return
    timeoutHandledRef.current = true
    void showToast('info', 'หมดเวลาการจองฝากทศนิยม')
    void cleanupReservation().finally(() => {
      setStep(1)
      resetTransientState(true)
      onClose()
    })
  }, [cleanupReservation, onClose, reservationId, resetTransientState, secondsLeft, selectedMethod])

  const validateAndSetFile = useCallback(async (file: File, onInvalid?: () => void) => {
    try {
      const result = await prepareSlipFileForUpload(file)
      if (!result.ok) {
        void showToast('error', result.message)
        onInvalid?.()
        return
      }

      setSelectedFile(result.file)
      if (previewUrl) URL.revokeObjectURL(previewUrl)
      setPreviewUrl(URL.createObjectURL(result.file))
      if (result.compressed) {
        void showToast('info', slipCompressedMessage(result.originalSize, result.file.size))
      }
    } catch {
      void showToast('error', 'ไม่สามารถประมวลผลไฟล์สลิปได้ กรุณาลองใหม่')
      onInvalid?.()
    }
  }, [previewUrl])

  const goToReview = () => {
    if (!selectedMethod) {
      void showToast('warning', 'กรุณาเลือกวิธีการฝาก')
      return
    }
    if (!Number.isFinite(enteredAmount) || enteredAmount <= 0) {
      void showToast('warning', 'กรุณาใส่จำนวนเงินที่ถูกต้อง')
      return
    }
    if (selectedMethod === 'decimal_reservation' && !/^\d+$/.test(amountInput.trim())) {
      void showToast('warning', 'ฝากทศนิยมต้องเป็นจำนวนเต็ม')
      return
    }
    if (!primaryUserBankAccount) {
      void showToast('warning', 'กรุณาเพิ่มบัญชีธนาคารของคุณก่อน')
      return
    }
    if (!isPaymentMode && !selectedManualAccount?.accountNumber) {
      void showToast('warning', 'ยังไม่พบบัญชีฝากที่ตรงกับจำนวนเงินนี้')
      return
    }

    if (selectedMethod !== 'decimal_reservation') {
      setReservationId(null)
      setReservationAmount('')
      setReservationRequestedAmount('')
      setSecondsLeft(null)
      decimalReservationStartedRef.current = false
      timeoutHandledRef.current = false
    }
    setPaymentTransaction(null)
    setPaymentError('')
    setPaymentRefreshMessage('')
    setDraftId(createDraftId())
    setStep(3)
  }

  const goToAmountStep = () => {
    if (!selectedMethod) {
      void showToast('warning', 'กรุณาเลือกวิธีการฝาก')
      return
    }
    setStep(2)
  }

  const submitSlipStep = async (attachSlip: boolean) => {
    if (!tenantId || !userId || !username || !selectedMethod || !draftId) {
      void showToast('error', 'ข้อมูลรายการฝากไม่ครบถ้วน')
      return
    }
    if (attachSlip && !selectedFile) {
      void showToast('error', 'กรุณาแนบสลิปก่อนส่งรายการ')
      return
    }
    if (!attachSlip && !allowSubmitWithoutSlip) {
      void showToast('error', 'รายการนี้ต้องแนบสลิปก่อนส่ง')
      return
    }
    if (!isPaymentMode && !selectedManualAccount?.accountNumber) {
      void showToast('error', 'ไม่พบบัญชีฝาก')
      return
    }
    if (isPaymentMode && !paymentTransaction?._id) {
      void showToast('error', 'ไม่พบรายการ Payment')
      return
    }

    setSubmitting(true)
    try {
      let uploadedSlipUrl = ''
      if (attachSlip && selectedFile) {
        const transferTime = new Date()
        const uploadResult = await slipStorageService.uploadSlip(tenantId, {
          userId,
          username,
          transferAmount: effectiveAmount,
          transferTime,
          transferDate: transferTime,
          slipFile: selectedFile,
        })

        if (!uploadResult.success) {
          throw new Error(uploadResult.message || 'อัปโหลดสลิปไม่สำเร็จ')
        }

        const uploadData = Array.isArray(uploadResult.data) ? uploadResult.data[0] : uploadResult.data
        uploadedSlipUrl = String(uploadData?.storageUrl || '')
      }

      if (isPaymentMode) {
        const result = await attachSlipToDepositTransaction(paymentTransaction!._id, {
          slipData: {
            imageUrl: uploadedSlipUrl,
            bankCode: primaryUserBankAccount?.bankCode,
            accountNumber: primaryUserBankAccount?.accountNumber,
          },
        })

        if (!result.success) {
          throw new Error(result.message || 'แนบสลิปไม่สำเร็จ')
        }

        handleModalCompleted(result.message || 'แนบสลิปเรียบร้อยแล้ว')
        return
      }

      const result = await createDeposit({
        amount: effectiveAmount,
        paymentMethod: 'bank_transfer',
        reference: draftId,
        ...(uploadedSlipUrl
          ? {
              slipData: {
                imageUrl: uploadedSlipUrl,
                bankCode: selectedManualAccount?.bankCode,
                accountNumber: selectedManualAccount?.accountNumber,
              },
            }
          : {}),
        metadata: {
          note: attachSlip ? 'bottom nav modal submitted deposit with slip attachment' : 'bottom nav modal submitted deposit without slip attachment',
          depositMethod: selectedMethod,
          selectedDepositAccountId: selectedManualAccount?._id,
          selectedDepositAccountNumber: selectedManualAccount?.accountNumber,
          selectedDepositAccountName: selectedManualAccount?.accountName,
          selectedDepositBankCode: selectedManualAccount?.bankCode,
          selectedDepositBankName: selectedManualAccount?.bankName,
          sourceChannel: attachSlip ? 'slip' : 'sms',
          submittedSlipImageUrl: uploadedSlipUrl || undefined,
        },
      })

      if (!result.success) {
        throw new Error(result.message || 'ส่งรายการฝากไม่สำเร็จ')
      }

      assertDepositResponseMatchesDraft(result, draftId)

      const nextMessage =
        result.data?.status === 'completed'
          ? 'ระบบเติมเครดิตให้เรียบร้อยแล้ว'
          : result.data?.approval?.required
            ? 'ระบบรับรายการฝากแล้ว และส่งต่อให้แอดมินตรวจสอบ'
            : result.recoveredExistingDeposit
              ? 'ระบบพบรายการฝากเดิมของคุณที่ส่งไว้แล้ว และรายการยังรอการตรวจสอบอยู่'
              : result.message || 'ส่งรายการฝากเรียบร้อยแล้ว'

      handleModalCompleted(nextMessage)
    } catch (error) {
      void showToast('error', error instanceof Error ? error.message : 'เกิดข้อผิดพลาดในการส่งรายการฝาก')
    } finally {
      setSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div style={modalShellStyle}>
      <div
        style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'auto', background: 'rgba(2, 6, 23, 0.42)' }}
        onClick={() => void requestClose()}
        aria-hidden="true"
      />
      <div style={modalCardStyle}>
        <div className="px-3 px-md-4 py-3" style={panelStyle}>
          <div className="d-flex align-items-start justify-content-between gap-3">
            <div>
              <div className="small text-white-50 mb-1">ฝากเงินผ่านเมนูล่าง</div>
              <h4 className="c_tertiary font_md mb-1">{getStepTitle(step, selectedMethod, isPaymentMode)}</h4>
              <p className="text-white-50 mb-0" style={{ maxWidth: 520 }}>
                {getStepDescription(step, selectedMethod, isPaymentMode)}
              </p>
            </div>
            <button
              type="button"
              className="border-0 rounded-circle d-inline-flex align-items-center justify-content-center"
              style={{
                width: 42,
                height: 42,
                background: 'rgba(255,255,255,0.08)',
                color: '#fff',
              }}
              onClick={() => void requestClose()}
              aria-label="ปิด"
            >
              <i className="fa fa-times" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="px-3 px-md-4 py-3" style={{ ...innerSurfaceStyle, borderRadius: 0, borderBottom: '1px solid rgba(148, 163, 184, 0.12)' }}>
          <div className="row g-2">
            {[
              'เลือกวิธีฝาก',
              'เลือกยอดฝาก',
              getReviewStepLabel(selectedMethod, isPaymentMode),
              'แนบสลิป',
            ].map((label, index) => {
              const active = step === index + 1
              return (
                <div key={label} className="col-6 col-md-3">
                  <div className={`h-100 ${active ? 'c_tertiary' : 'c_primary'}`} style={stepCardStyle(active)}>
                    <div className="small mb-1" style={active ? { color: 'rgba(255,255,255,0.82)' } : lightLabelStyle}>
                      Step {index + 1}
                    </div>
                    <div className="fw-bold">{label}</div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className="p-3 p-md-4" style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain' }}>
          {!primaryUserBankAccount ? (
            <div style={valueCardStyle}>
              <div className="mb-2" style={{ ...lightHeadingStyle, fontSize: '1.05rem', fontWeight: 700 }}>ยังไม่ได้ผูกบัญชีธนาคาร</div>
              <div style={lightTextStyle}>กรุณาเพิ่มบัญชีธนาคารของคุณก่อนเริ่มฝากเงินผ่านเมนูล่าง</div>
              <button
                type="button"
                className="link_pmr box_row justify-content-center font_rg bg_primary border_30 border-0 mt-3"
                onClick={() => {
                  onClose()
                  resetTransientState(true)
                  navigate('/member')
                }}
              >
                <div className="d-flex align-items-center c_tertiary font_md">
                  <h5 className="mb-0">ไปกรอกข้อมูลบัญชี</h5>
                </div>
              </button>
            </div>
          ) : (
            <>
              {step === 1 && (
                <div className="d-grid gap-3">
                  {configLoading ? (
                    <div style={valueCardStyle}>
                      <div style={lightTextStyle}>กำลังโหลดช่องทางฝากเงิน...</div>
                    </div>
                  ) : (
                    <div className="row g-3">
                      {methodOptions.map((option) => {
                        const active = selectedMethod === option.method
                        return (
                          <div key={option.method} className="col-12 col-md-6">
                            <button
                              type="button"
                              className="w-100 text-start border-0"
                              style={methodCardStyle(active, option.accent)}
                              onClick={() => setSelectedMethod(option.method)}
                            >
                              <div className="d-flex align-items-start gap-3">
                                <div
                                  className="d-inline-flex align-items-center justify-content-center rounded-circle"
                                  style={{
                                    width: 48,
                                    height: 48,
                                    background: `${option.accent}1a`,
                                    color: option.accent,
                                    fontSize: '1.1rem',
                                  }}
                                >
                                  <i className={option.icon} aria-hidden="true" />
                                </div>
                                <div className="flex-grow-1">
                                  <div style={{ ...lightHeadingStyle, fontSize: '1.05rem', fontWeight: 700 }}>{option.label}</div>
                                </div>
                                {active && (
                                  <div style={{ color: option.accent, fontSize: '1.1rem' }}>
                                    <i className="fa fa-check-circle" aria-hidden="true" />
                                  </div>
                                )}
                              </div>
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}

              {step === 2 && (
                <div className="d-grid gap-3">
                  <div style={valueCardStyle}>
                    <div className="small mb-2" style={lightLabelStyle}>วิธีฝากที่เลือก</div>
                    <div style={{ ...lightHeadingStyle, fontSize: '1.1rem', fontWeight: 700 }}>
                      {selectedMethod ? getMethodLabel(selectedMethod, isPaymentMode) : '-'}
                    </div>
                  </div>

                  <div style={valueCardStyle}>
                    <div className="small mb-2" style={lightLabelStyle}>ระบุจำนวนเอง</div>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={amountInput}
                      className="form-control"
                      style={{
                        minHeight: 56,
                        borderRadius: 18,
                        border: '1px solid rgba(148,163,184,0.35)',
                        fontSize: '1.35rem',
                        fontWeight: 700,
                        color: '#0f172a',
                      }}
                      placeholder="เช่น 100"
                      onChange={(event) => {
                        const raw = event.target.value
                        const nextValue = selectedMethod === 'decimal_reservation'
                          ? raw.replace(/[^\d]/g, '')
                          : raw.replace(/[^\d.]/g, '')
                        setAmountInput(nextValue)
                      }}
                    />
                    <div className="small mt-2" style={lightTextStyle}>
                      {selectedMethod === 'decimal_reservation'
                        ? 'ฝากทศนิยมรับเฉพาะจำนวนเต็ม แล้วระบบจะสุ่มเลขทศนิยมในขั้นถัดไป'
                        : 'ระบบจะเลือกบัญชีปลายทางตาม range ที่แอดมินตั้งไว้'}
                    </div>
                    {amountInput.trim() && Number.isFinite(enteredAmount) && enteredAmount > 0 && (
                      <div className="mt-3 d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill" style={{ background: 'rgba(37, 99, 235, 0.10)', color: '#1d4ed8', fontWeight: 700 }}>
                        <i className="fa fa-pen" aria-hidden="true" />
                        <span>ยอดที่ระบุ: {formatCurrencyLabel(enteredAmount)}</span>
                      </div>
                    )}
                  </div>

                  {presetAmounts.length > 0 && (
                  <div>
                    <div className="small mb-2" style={{ ...lightLabelStyle, paddingLeft: '0.25rem' }}>หรือเลือกยอดด่วน</div>
                    <div className="row g-2">
                    {presetAmounts.map((amount) => {
                      const selected = amountInput === String(amount)
                      return (
                        <div key={amount} className="col-4 col-md-3">
                          <button
                            type="button"
                            className="w-100 border-0"
                            style={amountButtonStyle(selected)}
                            onClick={() => setAmountInput(String(amount))}
                          >
                            <div className={selected ? 'text-white' : 'c_primary'} style={{ fontWeight: 800, fontSize: '1rem' }}>
                              {amount.toLocaleString()}
                            </div>
                            <div className="small" style={{ color: selected ? 'rgba(255,255,255,0.82)' : '#64748b' }}>บาท</div>
                          </button>
                        </div>
                      )
                    })}
                    </div>
                  </div>
                  )}

                  {!isPaymentMode && amountInput.trim() && !selectedManualAccount?.accountNumber && (
                    <div className="small text-warning">
                      ยังไม่พบบัญชีฝากที่ตรงกับจำนวนเงินนี้ กรุณาปรับยอดหรือเช็ก range ของบัญชีฝาก
                    </div>
                  )}
                </div>
              )}

              {step === 3 && (
                <div className="row g-3">
                  <div className="col-12 col-lg-5">
                    <div style={{ ...panelStyle, borderRadius: 24, padding: '1rem' }}>
                      <h5 className="c_tertiary font_md mb-3">ข้อมูลรายการฝาก</h5>
                      <div className="d-grid gap-3">
                        <div style={valueCardStyle}>
                          <div className="small mb-1" style={lightLabelStyle}>
                            {selectedMethod === 'decimal_reservation' ? 'จำนวนเงินที่ต้องโอน' : 'จำนวนเงิน'}
                          </div>
                          <div className="c_primary" style={{ fontSize: '1.25rem', fontWeight: 800 }}>{effectiveAmountDisplay}</div>
                        </div>
                        {selectedMethod === 'decimal_reservation' && (
                          <div style={valueCardStyle}>
                            <div className="small mb-1" style={lightLabelStyle}>ยอดตั้งต้น</div>
                            <div style={{ ...lightHeadingStyle, fontSize: '1.05rem', fontWeight: 700 }}>
                              {formatCurrencyLabel(Number(reservationRequestedAmount || amountInput || 0))}
                            </div>
                            <div className="small mt-2" style={lightTextStyle}>ระบบสุ่มเลขทศนิยมให้อัตโนมัติเพื่อแยกรายการฝาก</div>
                          </div>
                        )}
                        <div style={valueCardStyle}>
                          <div className="small mb-1" style={lightLabelStyle}>ช่องทางฝาก</div>
                          <div style={{ ...lightHeadingStyle, fontSize: '1.05rem', fontWeight: 700 }}>
                            {selectedMethod ? getMethodLabel(selectedMethod, isPaymentMode) : '-'}
                          </div>
                        </div>
                        <div style={valueCardStyle}>
                          <div className="small mb-1" style={lightLabelStyle}>บัญชีของคุณ</div>
                          <div style={{ ...lightHeadingStyle, fontSize: '1.05rem', fontWeight: 700 }}>{primaryUserBankAccount.bankName}</div>
                          <div className="mt-1" style={lightTextStyle}>{primaryUserBankAccount.accountName}</div>
                          <div className="mt-2" style={{ color: '#b45309', fontWeight: 800 }}>{primaryUserBankAccount.accountNumber}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-lg-7">
                    <div style={{ ...panelStyle, borderRadius: 24, padding: '1rem' }}>
                      <h5 className="c_tertiary font_md mb-3">
                        {isPaymentMode
                          ? 'ข้อมูลการชำระเงิน'
                          : selectedMethod === 'prompt_pay'
                            ? 'PromptPay ของระบบ'
                            : selectedMethod === 'true_money'
                              ? 'บัญชี TrueMoney ของระบบ'
                              : 'บัญชีปลายทางของระบบ'}
                      </h5>

                      {isPaymentMode ? (
                        <div className="d-grid gap-3">
                          {(paymentLoading || paymentRefreshing) && (
                            <div className="text-center" style={{ ...valueCardStyle, ...lightTextStyle }}>
                              {paymentLoading ? 'กำลังสร้าง QR ชำระเงิน...' : 'กำลังรีเฟรช QR ล่าสุด...'}
                            </div>
                          )}

                          {!paymentLoading && !paymentRefreshing && paymentError && (
                            <div style={valueCardStyle}>
                              <div className="small text-danger mb-3">{paymentError}</div>
                              <button
                                type="button"
                                className="link_pmr box_row justify-content-center font_rg bdc_primary bg_primary border_30 w-100 border-0"
                                onClick={() => void requestPaymentQr()}
                              >
                                <div className="d-flex align-items-center c_tertiary font_md">
                                  <h5 className="mb-0">ลองสร้าง QR ใหม่</h5>
                                </div>
                              </button>
                            </div>
                          )}

                          {!paymentLoading && !paymentRefreshing && !paymentError && (
                            <>
                              {paymentReuseSecondsLeft > 0 && (
                                <div
                                  style={{
                                    ...valueCardStyle,
                                    border: '1px solid rgba(245, 158, 11, 0.28)',
                                    background: 'linear-gradient(180deg, rgba(255,251,235,0.98) 0%, rgba(254,243,199,0.95) 100%)',
                                  }}
                                >
                                  <div className="small mb-1" style={{ color: '#92400e', fontWeight: 700 }}>
                                    {paymentGateway?.wasReused ? 'กำลังใช้รายการ Payment เดิม' : 'บัญชีนี้อยู่ในช่วง reuse รายการเดิม'}
                                  </div>
                                  <div style={{ color: '#b45309', fontWeight: 800, fontSize: '1.2rem' }}>{paymentReuseCountdownText}</div>
                                  <div className="small mt-2" style={{ color: '#78350f' }}>
                                    ระบบจะ reuse รายการเดิมตามเลขบัญชีและธนาคารของผู้ใช้ เพื่อให้ตรงกับข้อจำกัดของ provider
                                  </div>
                                </div>
                              )}

                              {hasPaymentQr ? (
                                <div className="text-center" style={valueCardStyle}>
                                  <div className="d-inline-flex rounded-4 bg-white p-3 shadow-sm">
                                    {paymentQrImage ? (
                                      <img
                                        src={paymentQrImage}
                                        alt="payment qr"
                                        className="img-fluid rounded-3"
                                        style={{ maxWidth: 260 }}
                                      />
                                    ) : (
                                      <QRCodeSVG value={paymentQrRawData} size={220} level="M" includeMargin />
                                    )}
                                  </div>
                                  <div className="small mt-3" style={lightTextStyle}>สแกน QR นี้เพื่อชำระเงินตามยอดที่เลือก</div>
                                  {paymentQrLink && (
                                    <div className="small mt-2">
                                      <a href={paymentQrLink} target="_blank" rel="noreferrer">
                                        เปิดลิงก์ชำระเงิน
                                      </a>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div style={valueCardStyle}>
                                  <div className="small mb-3" style={lightTextStyle}>
                                    ระบบสร้างรายการ Payment แล้ว แต่ยังไม่มี QR ล่าสุดจากผู้ให้บริการ
                                  </div>
                                  {paymentRefreshMessage && <div className="small text-warning mb-3">{paymentRefreshMessage}</div>}
                                  <button
                                    type="button"
                                    className="link_pmr box_row justify-content-center font_rg bdc_primary bg_primary border_30 w-100 border-0"
                                    disabled={!paymentTransaction?._id}
                                    onClick={() => void refreshExistingPayment()}
                                  >
                                    <div className="d-flex align-items-center c_tertiary font_md">
                                      <h5 className="mb-0">ดึง QR ล่าสุดอีกครั้ง</h5>
                                    </div>
                                  </button>
                                </div>
                              )}

                              <div style={valueCardStyle}>
                                <div className="small mb-1" style={lightLabelStyle}>เลขอ้างอิง Payment</div>
                                <div className="c_primary" style={{ fontWeight: 700 }}>{paymentReference}</div>
                              </div>

                              {paymentGateway?.bank && paymentGateway?.accountNumber && (
                                <div style={valueCardStyle}>
                                  <div className="small mb-1" style={lightLabelStyle}>บัญชีสำหรับชำระเงิน</div>
                                  <div style={{ ...lightHeadingStyle, fontSize: '1.05rem', fontWeight: 700 }}>{paymentGateway.bank}</div>
                                  <div className="mt-2 d-flex align-items-center justify-content-between gap-2 flex-wrap">
                                    <div style={{ color: '#b45309', fontWeight: 800 }}>{paymentGateway.accountNumber}</div>
                                    <button
                                      type="button"
                                      className="border-0 px-3 py-2 d-inline-flex align-items-center gap-2"
                                      style={{
                                        borderRadius: 999,
                                        background: 'rgba(37, 99, 235, 0.10)',
                                        color: '#1d4ed8',
                                        fontWeight: 700,
                                      }}
                                      onClick={() => navigator.clipboard?.writeText(paymentGateway.accountNumber || '').catch(() => {})}
                                    >
                                      <i className="fa fa-copy" aria-hidden="true" />
                                      <span>Copy</span>
                                    </button>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="d-grid gap-3">
                          {selectedMethod === 'prompt_pay' && promptPayQrValue && (
                            <div className="text-center" style={valueCardStyle}>
                              <div className="small mb-3" style={lightLabelStyle}>QR PromptPay</div>
                              <div className="d-inline-flex rounded-4 bg-white p-3 shadow-sm">
                                <QRCodeSVG value={promptPayQrValue} size={220} level="M" includeMargin />
                              </div>
                              <div className="small mt-3" style={lightTextStyle}>สแกน QR นี้เพื่อโอนผ่าน PromptPay ตามยอดที่เลือก</div>
                            </div>
                          )}

                          {selectedMethod !== 'prompt_pay' && (
                            <div style={valueCardStyle}>
                              <div className="small mb-1" style={lightLabelStyle}>
                                {selectedMethod === 'true_money' ? 'บัญชี TrueMoney ของเว็บ' : 'บัญชีปลายทางของเว็บ'}
                              </div>
                              <div className="d-flex align-items-center gap-3 flex-wrap">
                                {selectedManualAccount ? (
                                  <img
                                    src={getDepositAccountLogoUrl(selectedManualAccount)}
                                    alt={selectedManualAccount.bankName}
                                    className="img-fluid rounded-4 bg-white p-2"
                                    style={{ width: 86, height: 86, objectFit: 'contain' }}
                                  />
                                ) : null}
                                <div className="flex-grow-1">
                                  <div style={{ ...lightHeadingStyle, fontSize: '1.05rem', fontWeight: 700 }}>
                                    {selectedManualAccount?.bankName || '-'}
                                  </div>
                                  <div className="mt-1" style={lightTextStyle}>
                                    {selectedManualAccount?.accountName || '-'}
                                  </div>
                                  <div className="mt-2 d-flex align-items-center justify-content-between gap-2 flex-wrap">
                                    <div style={{ color: '#b45309', fontWeight: 800 }}>
                                      {selectedManualAccount?.accountNumber || '-'}
                                    </div>
                                    {selectedManualAccount?.accountNumber && (
                                      <button
                                        type="button"
                                        className="border-0 px-3 py-2 d-inline-flex align-items-center gap-2"
                                        style={{
                                          borderRadius: 999,
                                          background: 'rgba(37, 99, 235, 0.10)',
                                          color: '#1d4ed8',
                                          fontWeight: 700,
                                        }}
                                        onClick={() => navigator.clipboard?.writeText(selectedManualAccount.accountNumber).catch(() => {})}
                                      >
                                        <i className="fa fa-copy" aria-hidden="true" />
                                        <span>Copy</span>
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {selectedMethod === 'decimal_reservation' && (
                            <div style={valueCardStyle}>
                              <div className="small mb-1" style={lightLabelStyle}>เวลาที่เหลือสำหรับการจอง</div>
                              <div className="c_primary" style={{ fontSize: '1.55rem', fontWeight: 800 }}>
                                {secondsLeft == null ? '--:--' : formatTime(secondsLeft)}
                              </div>
                              <div className="small mt-2" style={lightTextStyle}>
                                กรุณาโอนตามยอดนี้ภายในเวลาที่กำหนด แล้วไปแนบสลิปในขั้นตอนถัดไป
                              </div>
                            </div>
                          )}

                          {depositFlow.automationSettingsWarning && (
                            <div className="small text-warning">{depositFlow.automationSettingsWarning}</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {step === 4 && (
                <div className="row g-3">
                  <div className="col-12 col-lg-5">
                    <div style={{ ...panelStyle, borderRadius: 24, padding: '1rem' }}>
                      <h5 className="c_tertiary font_md mb-3">ข้อมูลรายการ</h5>
                      <div className="d-grid gap-3">
                        <div style={valueCardStyle}>
                          <div className="small mb-1" style={lightLabelStyle}>จำนวนเงิน</div>
                          <div className="c_primary" style={{ fontSize: '1.25rem', fontWeight: 800 }}>{effectiveAmountDisplay}</div>
                        </div>
                        <div style={valueCardStyle}>
                          <div className="small mb-1" style={lightLabelStyle}>ช่องทางฝาก</div>
                          <div style={{ ...lightHeadingStyle, fontSize: '1.05rem', fontWeight: 700 }}>
                            {selectedMethod ? getMethodLabel(selectedMethod, isPaymentMode) : '-'}
                          </div>
                        </div>
                        {!(selectedMethod === 'prompt_pay' && !isPaymentMode) && (
                          <div style={valueCardStyle}>
                            <div className="small mb-1" style={lightLabelStyle}>
                              {isPaymentMode ? 'อ้างอิง Payment' : 'บัญชีปลายทางของเว็บ'}
                            </div>
                            <div style={{ ...lightHeadingStyle, fontSize: '1.05rem', fontWeight: 700 }}>
                              {isPaymentMode ? paymentGateway?.bank || 'Payment' : selectedManualAccount?.bankName || '-'}
                            </div>
                            <div className="mt-1" style={lightTextStyle}>
                              {isPaymentMode ? 'แนบสลิปหลังชำระเงินผ่าน QR' : selectedManualAccount?.accountName || '-'}
                            </div>
                            <div className="mt-2" style={{ color: '#b45309', fontWeight: 800 }}>
                              {isPaymentMode ? paymentReference : selectedManualAccount?.accountNumber || '-'}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-lg-7">
                    <div style={{ ...panelStyle, borderRadius: 24, padding: '1rem' }}>
                      <h5 className="c_tertiary font_md mb-3">
                        {isPaymentMode ? 'แนบสลิปการชำระเงิน' : `แนบสลิป ${allowSubmitWithoutSlip ? '(ไม่บังคับ)' : '(บังคับ)'}`}
                      </h5>
                      <div
                        className="border_30 px-3 py-4 text-center"
                        style={{
                          ...innerSurfaceStyle,
                          border: isDragOver ? '1px solid rgba(37, 99, 235, 0.35)' : '1px dashed rgba(148, 163, 184, 0.45)',
                          transform: isDragOver ? 'translateY(-1px)' : 'translateY(0)',
                          transition: 'all 0.2s ease',
                        }}
                        onDragOver={(event) => {
                          event.preventDefault()
                          setIsDragOver(true)
                        }}
                        onDragLeave={() => setIsDragOver(false)}
                        onDrop={(event) => {
                          event.preventDefault()
                          setIsDragOver(false)
                          const file = event.dataTransfer.files?.[0]
                          if (!file) return
                          void validateAndSetFile(file)
                        }}
                      >
                        <div className="c_primary mb-2">
                          <i className="fa fa-cloud-upload-alt" style={{ fontSize: '2rem' }} />
                        </div>
                        <div className="c_primary font_md mb-2">ลากไฟล์มาวาง หรือเลือกสลิปจากเครื่อง</div>
                        <div className="small mb-3" style={lightTextStyle}>{SLIP_UPLOAD_HINT} (ระบบปรับขนาดอัตโนมัติเมื่อไฟล์ใหญ่)</div>
                        <button
                          type="button"
                          className="link_pmr box_row justify-content-center font_rg bg_primary border_30 border-0 mx-auto"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <div className="d-flex align-items-center c_tertiary font_md">
                            <h5 className="mb-0">เลือกไฟล์สลิป</h5>
                          </div>
                        </button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                          className="d-none"
                          onChange={(event) => {
                            const file = event.target.files?.[0]
                            if (!file) return
                            void validateAndSetFile(file, () => {
                              event.target.value = ''
                            })
                          }}
                        />
                      </div>

                      {previewUrl && (
                        <div className="mt-3">
                          <div className="small mb-2" style={lightLabelStyle}>ตัวอย่างสลิป</div>
                          <div className="border_30 p-2" style={innerSurfaceStyle}>
                            <img src={previewUrl} alt="slip preview" className="img-fluid rounded-3" />
                          </div>
                        </div>
                      )}

                      {selectedFile && (
                        <div className="small mt-2" style={lightTextStyle}>
                          ไฟล์ที่เลือก: {selectedFile.name}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-3 px-md-4 py-3 border-top" style={{ borderColor: 'rgba(148,163,184,0.12)' }}>
          <div className="d-flex flex-column flex-md-row gap-2">
            {step === 1 ? (
              <>
                <button
                  type="button"
                  className="link_pmr box_row justify-content-center font_rg bg_secondary border_30 w-100 border-0"
                  onClick={() => void requestClose()}
                >
                  <div className="d-flex align-items-center c_secondary font_md">
                    <h5 className="mb-0">ปิด</h5>
                  </div>
                </button>
                <button
                  type="button"
                  className="link_pmr box_row justify-content-center font_rg bdc_primary bg_primary border_30 w-100 border-0"
                  onClick={goToAmountStep}
                >
                  <div className="d-flex align-items-center c_tertiary font_md">
                    <h5 className="mb-0">ไปขั้นตอนเลือกยอดฝาก</h5>
                  </div>
                </button>
              </>
            ) : step === 2 ? (
              <>
                <button
                  type="button"
                  className="link_pmr box_row justify-content-center font_rg bg_secondary border_30 w-100 border-0"
                  onClick={() => setStep(1)}
                >
                  <div className="d-flex align-items-center c_secondary font_md">
                    <h5 className="mb-0">กลับไปเลือกวิธีฝาก</h5>
                  </div>
                </button>
                <button
                  type="button"
                  className="link_pmr box_row justify-content-center font_rg bdc_primary bg_primary border_30 w-100 border-0"
                  onClick={goToReview}
                >
                  <div className="d-flex align-items-center c_tertiary font_md">
                    <h5 className="mb-0">ยืนยันยอดและดูรายละเอียด</h5>
                  </div>
                </button>
              </>
            ) : step === 3 ? (
              <>
                <button
                  type="button"
                  className="link_pmr box_row justify-content-center font_rg bg_secondary border_30 w-100 border-0"
                  onClick={async () => {
                    if (selectedMethod === 'decimal_reservation' && reservationId) {
                      await cleanupReservation()
                    }
                    resetTransientState(false)
                    setStep(2)
                  }}
                >
                  <div className="d-flex align-items-center c_secondary font_md">
                    <h5 className="mb-0">กลับไปแก้ไข</h5>
                  </div>
                </button>
                <button
                  type="button"
                  className="link_pmr box_row justify-content-center font_rg bdc_primary bg_primary border_30 w-100 border-0"
                  disabled={!canProceedToSlip}
                  onClick={() => setStep(4)}
                >
                  <div className="d-flex align-items-center c_tertiary font_md">
                    <h5 className="mb-0">ไปขั้นตอนแนบสลิป</h5>
                  </div>
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="link_pmr box_row justify-content-center font_rg bg_secondary border_30 w-100 border-0"
                  onClick={() => setStep(3)}
                  disabled={submitting}
                >
                  <div className="d-flex align-items-center c_secondary font_md">
                    <h5 className="mb-0">กลับไปหน้าสรุป</h5>
                  </div>
                </button>
                {allowSubmitWithoutSlip && (
                  <button
                    type="button"
                    className="link_pmr box_row justify-content-center font_rg bg_secondary border_30 w-100 border-0"
                    onClick={() => void submitSlipStep(false)}
                    disabled={submitting}
                  >
                    <div className="d-flex align-items-center c_secondary font_md">
                      <h5 className="mb-0">{submitting ? 'กำลังส่งรายการ...' : 'ส่งรายการโดยยังไม่แนบสลิป'}</h5>
                    </div>
                  </button>
                )}
                <button
                  type="button"
                  className="link_pmr box_row justify-content-center font_rg bdc_primary bg_primary border_30 w-100 border-0"
                  onClick={() => void submitSlipStep(true)}
                  disabled={submitting}
                >
                  <div className="d-flex align-items-center c_tertiary font_md">
                    <h5 className="mb-0">
                      {submitting
                        ? 'กำลังส่งรายการ...'
                        : isPaymentMode
                          ? 'แนบสลิปให้รายการนี้'
                          : 'ส่งรายการฝากพร้อมแนบสลิป'}
                    </h5>
                  </div>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default BottomDepositFlowModal
